import { cn } from "@/lib/utils";

export function resolveActionStatus(
  action: string | null | undefined,
  actionResult: string | null | undefined,
): { label: string; neutral?: boolean } {
  const result = actionResult && actionResult !== "none" ? actionResult : null;
  const act = action && action !== "none" ? action : null;

  if (result === "blocked") return { label: "Blocked" };
  if (result === "ignored") return { label: "Ignored" };
  if (result === "block_failed") return { label: "Block failed" };
  if (result === "whitelisted") return { label: "Whitelisted" };
  if (act === "block") return { label: "Block pending" };
  if (act === "ignore") return { label: "Ignore pending" };
  return { label: "Not actioned", neutral: true };
}

export const TYPE_CONFIG = {
  theft_for_restreaming: {
    label: "Theft",
    hex: "#fb7185",
    text: "text-rose-400",
    bg: "bg-rose-500/10",
    border: "border-rose-500/25",
  },
  restreaming: {
    label: "Restreaming",
    hex: "#a78bfa",
    text: "text-violet-400",
    bg: "bg-violet-500/10",
    border: "border-violet-500/25",
  },
  cdn_leeching: {
    label: "CDN Leeching",
    hex: "#fbbf24",
    text: "text-amber-400",
    bg: "bg-amber-500/10",
    border: "border-amber-500/25",
  },
} as const;

export const STATUS_CONFIG = {
  open: { label: "Open" },
  in_progress: { label: "In progress" },
  resolved: { label: "Resolved" },
} as const;

export const SEVERITY_CONFIG = {
  critical: {
    label: "Critical",
    text: "text-rose-400",
    bg: "bg-rose-500/10",
    border: "border-rose-500/25",
  },
  high: {
    label: "High",
    text: "text-orange-400",
    bg: "bg-orange-500/10",
    border: "border-orange-500/25",
  },
  medium: {
    label: "Medium",
    text: "text-amber-400",
    bg: "bg-amber-500/10",
    border: "border-amber-500/25",
  },
  low: {
    label: "Low",
    text: "text-emerald-400",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/25",
  },
} as const;

export function InfringementTypeBadge({ type }: { type: string }) {
  const cfg = TYPE_CONFIG[type as keyof typeof TYPE_CONFIG];
  if (!cfg) return <span className="text-xs text-muted">—</span>;
  return (
    <span
      className={cn(
        "inline-flex items-center px-1.5 py-0.5 rounded-md text-[11px] font-semibold border",
        cfg.bg,
        cfg.text,
        cfg.border,
      )}
    >
      {cfg.label}
    </span>
  );
}

export function InfringementStatusBadge({ status }: { status: string }) {
  const label = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG]?.label ?? status;
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[11px] font-medium bg-surface-elevated border border-border/50 text-secondary">
      {label}
    </span>
  );
}

export function InfringementActionBadge({
  action,
  actionResult,
}: {
  action: string | null | undefined;
  actionResult: string | null | undefined;
}) {
  const s = resolveActionStatus(action, actionResult);
  if (s.neutral) return <span className="text-xs text-muted/40">—</span>;

  const result = actionResult && actionResult !== "none" ? actionResult : null;
  const act = action && action !== "none" ? action : null;

  let color = "text-secondary";
  if (result === "blocked") color = "text-rose-400";
  else if (result === "ignored") color = "text-secondary";
  else if (result === "block_failed") color = "text-rose-500/70";
  else if (result === "whitelisted") color = "text-emerald-400";
  else if (act === "block" || act === "ignore") color = "text-amber-400";
  return <span className={cn("text-xs", color)}>{s.label}</span>;
}

export function InfringementSeverityBadge({ severity }: { severity: string }) {
  const cfg = SEVERITY_CONFIG[severity as keyof typeof SEVERITY_CONFIG];
  if (!cfg) return <span className="text-xs text-muted">—</span>;
  return (
    <span
      className={cn(
        "inline-flex items-center px-1.5 py-0.5 rounded-md text-[11px] font-semibold border",
        cfg.bg,
        cfg.text,
        cfg.border,
      )}
    >
      {cfg.label}
    </span>
  );
}

/** Horizontal progress bar for confidence scores (0–1). */
export function ConfidenceBar({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const barCls = value >= 0.9 ? "bg-rose-400" : value >= 0.75 ? "bg-amber-400" : "bg-emerald-400";
  const textCls =
    value >= 0.9 ? "text-rose-400" : value >= 0.75 ? "text-amber-400" : "text-secondary";
  return (
    <div className="flex items-center gap-2.5">
      <div className="flex-1 h-1.5 bg-border/20 rounded-full overflow-hidden">
        <div className={cn("h-full rounded-full", barCls)} style={{ width: `${pct}%` }} />
      </div>
      <span className={cn("text-xs tabular-nums font-medium w-7 text-right shrink-0", textCls)}>
        {pct}%
      </span>
    </div>
  );
}
